import type {
  Agency,
  AvailabilityResult,
  BlogPost,
  BlogPostSummary,
  Booking,
  BookingPayment,
  CancelOutcome,
  CancelPreview,
  CancelReason,
  CancelRequest,
  DownloadFile,
  ExtensionRequest,
  PartnerStatus,
  Review,
  TripExtension,
  TripRatingInput,
  VehicleResultsPage,
  CouponCheck,
  ContactMessage,
  Currency,
  Cart,
  CategorySummary,
  City,
  Destination,
  EligibilityRequest,
  EligibilityRequestInput,
  Experience,
  ExperienceBookingInput,
  ExperienceDay,
  ExperienceFilters,
  ExperienceQuote,
  ExperienceSelection,
  ExperienceSlot,
  ExperienceSummary,
  Favourites,
  Message,
  MessagePage,
  PartnerLead,
  PaymentConfig,
  PaymentFinalizeResult,
  PlatformConfig,
  ProfileStats,
  ProfileUpdate,
  Quote,
  RefundTier,
  Thread,
  Trip,
  User,
  Vehicle,
  VehicleBookingInput,
  VehicleFilters,
  VehicleKind,
  VehicleSearch,
  VehicleSummary,
} from './domain';

/**
 * Everything the UI can ask the backend. One implementation per data source:
 * - services/mock: in-memory demo data (phase 1)
 * - services/http: the Cardnd backend (phase 4), mapping API responses into the domain types
 */
export interface Api {
  config: {
    get(): Promise<PlatformConfig>;
  };
  cities: {
    vehicles(kind: VehicleKind): Promise<City[]>;
    experiences(): Promise<City[]>;
    destinations(): Promise<Destination[]>;
  };
  vehicles: {
    search(search: VehicleSearch, filters: VehicleFilters): Promise<VehicleSummary[]>;
    categories(kind: VehicleKind, city: string): Promise<CategorySummary[]>;
    featured(city: string): Promise<VehicleSummary[]>;
    get(id: string): Promise<Vehicle>;
    similar(id: string): Promise<VehicleSummary[]>;
    /**
     * `currency` (PR-11): the chosen currency, sent to the server quote, which then adds the online charge in it
     * (`Quote.converted`); default MAD. A guest estimate is always MAD only.
     */
    quote(vehicleId: string, search: VehicleSearch, currency?: Currency): Promise<Quote>;
    /** Times are `HH:mm`; without them the whole days are checked. */
    availability(
      vehicleId: string,
      start: string,
      end: string,
      pickupTime?: string,
      returnTime?: string,
    ): Promise<AvailabilityResult>;
    refundPolicy(start: string, pickupTime: string): Promise<RefundTier[]>;
  };
  agencies: {
    get(slug: string): Promise<Agency>;
  };
  blog: {
    list(): Promise<BlogPostSummary[]>;
    get(id: string): Promise<BlogPost>;
  };
  experiences: {
    list(filters: ExperienceFilters): Promise<ExperienceSummary[]>;
    categories(city: string): Promise<{ label: string; count: number }[]>;
    get(id: string): Promise<Experience>;
    days(id: string, from: string): Promise<ExperienceDay[]>;
    quote(selection: ExperienceSelection): Promise<ExperienceQuote>;
    /** P6: open departures from `from` to `to` included (`GET /api/client/activities/:id/slots`). */
    slots(id: string, from: string, to: string): Promise<ExperienceSlot[]>;
  };
  bookings: {
    createVehicle(input: VehicleBookingInput): Promise<Booking>;
    /**
     * P6: `POST /api/client/activity-reservations` with the full checkout. The same input again in this tab
     * resumes the unpaid reservation (its payment intent) instead of creating another (F8).
     */
    createExperience(input: ExperienceBookingInput): Promise<Booking>;
    get(id: string): Promise<Booking>;
    trips(): Promise<Trip[]>;
    carts(): Promise<Cart[]>;
    cancel(tripId: string, reason?: CancelReason): Promise<void>;
    /** Hides an unpaid cart from the client's history (the platform keeps it). */
    removeCart(cartId: string): Promise<void>;
    rate(tripId: string, stars: number, comment?: string): Promise<void>;
  };
  favourites: {
    list(): Promise<Favourites>;
    ids(): Promise<string[]>;
    toggle(id: string): Promise<boolean>;
  };
  auth: {
    session(): Promise<User | null>;
    login(email: string, password: string): Promise<User>;
    register(input: { name: string; email: string; password: string; marketing: boolean }): Promise<User>;
    /** Google Identity Services ID token (`credential`) → `POST /api/client/auth/google` (P7). */
    google(credential: string): Promise<User>;
    logout(): Promise<void>;
    forgotPassword(email: string): Promise<void>;
    resetPassword(code: string, password: string): Promise<void>;
  };
  profile: {
    stats(): Promise<ProfileStats>;
    update(input: ProfileUpdate): Promise<User>;
  };
  messages: {
    threads(): Promise<Thread[]>;
    send(threadId: string, text: string): Promise<Message>;
    markRead(threadId: string): Promise<void>;
  };
  partner: {
    submit(lead: PartnerLead): Promise<void>;
  };
  newsletter: {
    subscribe(email: string): Promise<void>;
  };

  /* ── Payments (P4): the Stripe card step, shared by the vehicle checkout, cart resume and experiences ── */
  payments: {
    /** Publishable key and readiness (`GET /api/payments/config`). */
    config(): Promise<PaymentConfig>;
    /** After the card step: the server re-reads the intent from Stripe and moves the reservation on. */
    finalize(paymentIntentId: string): Promise<PaymentFinalizeResult>;
    /**
     * Re-opens the payment of an unpaid vehicle reservation (a cart; INVENTORY Appendix A `resumeCart`):
     * the server reuses a usable intent or mints a new one. Errors carry the server code (PAY-11).
     */
    resume(reservationId: string, currency: Currency): Promise<BookingPayment>;
  };

  /* ── Coupons (P3) ── */
  coupons: {
    /** `GET /api/coupons/check`: the label and percentage, or a French error. */
    check(code: string): Promise<CouponCheck>;
  };

  /* ── Trip actions (P5): cancel with the server preview, extension, rating, voucher, rating prompt ── */
  trips: {
    /** Vehicles: `GET /api/reservations/:id/cancel-preview`. Experiences have no preview (policy text on the trip). */
    cancelPreview(tripId: string): Promise<CancelPreview>;
    /** Vehicles `PATCH …/client-cancel {reason, note, alertAnswers}`; experiences `PATCH …/activity-reservations/:id/cancel`. */
    cancel(tripId: string, request: CancelRequest): Promise<CancelOutcome>;
    /** `POST /api/reservations/:id/request-extension`: the agency confirms later; the server prices it. */
    requestExtension(tripId: string, request: ExtensionRequest): Promise<TripExtension>;
    /** The five criteria of the trip's kind plus a comment (required by the server). */
    rate(tripId: string, rating: TripRatingInput): Promise<void>;
    /** Voucher PDF (vehicle `bon-reservation-<code>.pdf`, experience `cardnd-voucher-<code>.pdf`). */
    voucherPdf(tripId: string): Promise<DownloadFile>;
    /** The first completed, unrated vehicle trip (legacy home prompt STA-5 → RAT-7), or null. */
    toRate(): Promise<Trip | null>;
  };

  /* ── Account (P13): the client's reviews and partner status ── */
  reviews: {
    /** `GET /api/ratings/client`. */
    list(): Promise<Review[]>;
    /** `DELETE /api/ratings/:id`. */
    remove(reviewId: string): Promise<void>;
  };
  account: {
    /** `GET /api/client/auth/partner/status`. */
    partnerStatus(): Promise<PartnerStatus>;
  };

  /* ── Results (P15): server paging and legacy URL lookups ── */
  results: {
    /** One page of the search (cars: the server's `page`/`limit`; motorbikes: one page, the API has no paging). */
    page(search: VehicleSearch, filters: VehicleFilters, page: number): Promise<VehicleResultsPage>;
    /** Name of an experience category by id (legacy `/activities?categoryId=`), or null. */
    activityCategory(id: string): Promise<string | null>;
  };

  /*
   * ── Chat (P8): older pages and the support rating. The support thread is created by its first
   * `messages.send` (thread id `SUPPORT_DRAFT_ID`); live updates come from services/realtime. ──
   */
  chat: {
    /** The 50 messages before `beforeId` (`GET /api/conversations/:id/messages?before=`). */
    older(threadId: string, beforeId: string): Promise<MessagePage>;
    /** Satisfaction 1–5 of a resolved support thread (`PATCH /api/conversations/:id/rate`). */
    rate(threadId: string, rating: number): Promise<void>;
  };

  /* ── Marketing consent (P11): `PATCH /api/client/auth/marketing-consent {marketingConsent:true}` ── */
  marketing: {
    accept(): Promise<void>;
  };

  /*
   * ── Contact form (P14): `POST /api/send-email` with the legacy body `{name, email, subject, message}`
   * (subject «[URGENT] …» when urgent, message «Téléphone: …» first when a phone is given). The server
   * answers before it sends the e-mails: success means received, not delivered. ──
   */
  contact: {
    send(message: ContactMessage): Promise<void>;
  };

  /* ── Experience booking (P6): free-entry requests and provider tickets ── */
  experienceBooking: {
    /** `POST /api/client/activity-eligibility` (422 refused, 429 over 10 pending). */
    requestEligibility(input: EligibilityRequestInput): Promise<EligibilityRequest>;
    /** `GET /api/client/activity-eligibility/:id` (the client's own request). */
    eligibility(id: string): Promise<EligibilityRequest>;
    /** `POST /api/client/activity-reservations/:id/provider-tickets/resend` (409 not issued, 429 under 60 s). */
    resendTickets(bookingId: string): Promise<void>;
  };

  /*
   * ── Cross-sell (API-ACT-16): experiences for a stay. `GET /api/client/cross-sell/activities?city&from&to&limit`:
   * the city's active experiences with an open departure between `from` and `to` (both days included; the next
   * 14 days without dates), best rated first, at most 12. [] when the city has none or activities are off. ──
   */
  crossSell: {
    experiences(query: CrossSellQuery): Promise<ExperienceSummary[]>;
  };
}

/** What the cross-sell rail asks for: the stay's city (as the backend stores it) and days (`yyyy-MM-dd`). */
export interface CrossSellQuery {
  city: string;
  from?: string;
  to?: string;
  /** 6 by default (the server's), 12 at most. */
  limit?: number;
}

export type ApiErrorCode = 'unauthorized' | 'not_found' | 'unavailable' | 'invalid' | 'network';

/** What an error can carry besides its message (e.g. the reservation a failed payment left behind). */
export interface ApiErrorDetails {
  /** HTTP status, when the error came from the server. */
  status?: number;
  /** Backend error code (`EMAIL_EXISTS`, `RATE_UNAVAILABLE`…), when it sent one. */
  serverCode?: string;
  /** Reservation created before the payment step failed (kept: retried, never duplicated). */
  reservationId?: string;
}

/** Errors the UI knows how to show. Message is user-facing French. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: ApiErrorCode = 'invalid',
    readonly details: ApiErrorDetails = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
